/// <reference path="_references.ts"/>

module App.Forms {
	import ToastService = App.Utils.ToastService;

	abstract class FormDetailControllerBase {
		static $inject: string[] = [
			'Validations',
			'$q', '$state',
			'FormService', 'UploadService', 'OfficeService', 'PermissionService', 'SelectedAppointment', 'TitleBarService',
			'ToastService',
		];

		public isNew: boolean;
		public isLoading: boolean = true;
		public isSaving: boolean = false;
		public isItemForm: boolean;
		public instanceCount: number = 0;
		public instance: FormInstance;
		public service_request_id: number;
		public email: string;
		public phone: string;
		public isEmail: boolean = false;

		public files: {
			[eId: string]: File[]
		} = {};

		public htmlForm: ng.IFormController;
		public attemptedSubmit: boolean = false;

		public hasPermission: boolean = true;

		protected abstract getInstance(): ng.IPromise<FormInstance>;
		protected abstract saveInstance(data: any, email?: string, phone?: string): ng.IPromise<FormInstance>;

		constructor(
			public Validations: Validations.Validations,
			protected $q: ng.IQService,
			protected $state: ng.ui.IStateService,
			protected FormService: FormService,
			protected UploadService: App.Helpers.UploadService,
			protected OfficeService: App.Utils.OfficeService,
			protected PermissionService: App.Helpers.PermissionService,
			protected SelectedAppointment: App.Appointments.SelectedAppointment,
			protected TitleBarService: App.Helpers.TitleBarService,
			protected ToastService: ToastService,
		) {
			TitleBarService.set('Forms');
			this.isItemForm = Boolean($state.params.item);

			this.hasPermission = this.PermissionService.hasAnyPermission(['web.field.left-nav.service-request-forms', 'web.office.service-requests.service-request.tech-view']);

			let countPromise = OfficeService.getAppointment()
				.then((resp) => resp.data)
				.catch(() => SelectedAppointment.getAppointment())
				.then((appt) => {
					this.service_request_id = appt.service_request.id;
					this.email = appt.service_request.customer.emails.length
						? appt.service_request.customer.emails[0].email
						: '';
					this.phone = appt.service_request.customer.phone_numbers.length
						? appt.service_request.customer.phone_numbers[0].number
						: '';

					if (this.isItemForm) {
						return null;
					}

					return this.FormService.getInstances(
						this.service_request_id,
						{ size: 0 }
					);
				});

			this.$q
				.all({
					count: countPromise,
					instance: this.getInstance()
				})
				.then((data) => {
					this.instanceCount = this.isItemForm
						? 0
						: Number(data.count.headers().count);

					this.instance = data.instance;
					this.isLoading = false;
				})
				.catch((err) => {
					this.ToastService.showError('Error loading form: ', err);
					this.goHome();
				})
		}

		public getBackSref(): string {
			return this.isItemForm
				? 'zipSolutions.maintenance.details({itemId:' + this.$state.params.item + '})'
				: '^'
		}

		public goBack(): void {
			if (this.isItemForm) {
				this.$state.go('zipSolutions.maintenance.details', { itemId: this.$state.params.item });
			} else {
				this.$state.go('^');
			}
		}

		public goHome(): void {
			if (this.isItemForm) {
				this.goBack();
			} else {
				this.$state.go('home');
			}
		}

		public canGoBack(): boolean {
			return this.isItemForm || (this.instanceCount > 0);
		}

		public clearCustomerSignature(): void {
			this.instance.customer_signature = '';
			this.htmlForm.customer_signature.$setTouched();
		}

		public clearTechSignature(): void {
			this.instance.tech_signature = '';
			this.htmlForm.tech_signature.$setTouched();
		}

		public showCustomerSignature(): boolean {
			return SignatureType[this.instance.version.customer_signature] !== SignatureType.NONE;
		}

		public showTechSignature(): boolean {
			return SignatureType[this.instance.version.tech_signature] !== SignatureType.NONE;
		}

		public isCustomerSignatureRequired(): boolean {
			return SignatureType[this.instance.version.customer_signature] === SignatureType.REQUIRED && this.instance.version.read_only;
		}

		public isTechSignatureRequired(): boolean {
			return SignatureType[this.instance.version.tech_signature] === SignatureType.REQUIRED;
		}

		public isFormDisabled(form: ng.IFormController): boolean {
			return this.isSaving;
		}

		public upgrade(): void {
			componentHandler.upgradeAllRegistered();
		}

		public formSubmit(form: ng.IFormController, email?: string): void {
			this.attemptedSubmit = true;

			if (this.isFormDisabled(form)
				|| Boolean(form && form.$invalid)
				|| (this.isEmail && email && !this.Validations.email.pattern.test(email))
				|| (!this.isEmail && email && !this.Validations.phone.pattern.test(email))
			) {
				this.ToastService.show('Please fix the errors noted above', ToastService.ERROR_SELECTOR);
				return;
			}

			this.isSaving = true;

			let data: any = angular.copy(this.instance);
			data.version = data.version.id;

			this.saveInstance(data, this.isEmail ? email : null, this.isEmail ? null : email)
				.then((instance) => {
					this.uploadImages(instance.id);
					this.goBack();
					this.instance = instance;
					componentHandler.upgradeAllRegistered();
				})
				.catch((err) => this.ToastService.showError('Failed to save form: ', err))
				.finally(() => this.isSaving = false);
		}

		public getAnswerIndex(id: number): number {
			let index = this.instance.answers.findIndex((a) => a.element === id);

			if (index === -1) {
				this.instance.answers.push({
					id: null,
					element: id,
					answer: '',
					images: []
				});
				index = this.instance.answers.length - 1;
			}

			return index;
		}

		public getFileFor(type: keyof typeof FormElementType): string|null {
			switch (FormElementType[type]) {
				case FormElementType.Checkbox:
					return 'html/forms/inputs/checkbox.tpl.html';

				case FormElementType.Number:
				case FormElementType.Text:
					return 'html/forms/inputs/input.tpl.html';

				case FormElementType.Header:
					return 'html/forms/inputs/header.tpl.html';

				case FormElementType.StaticText:
					return 'html/forms/inputs/static-text.tpl.html';

				case FormElementType.Dropdown:
					return 'html/forms/inputs/select.tpl.html';

				case FormElementType.MultiSelect:
					return 'html/forms/inputs/multi-select.tpl.html';

				case FormElementType.TextArea:
					return 'html/forms/inputs/textarea.tpl.html';

				case FormElementType.CheckList:
					return 'html/forms/inputs/checklist.tpl.html';

				case FormElementType.PassFail:
					return 'html/forms/inputs/passfail.tpl.html';

				default:
					return null;
			}
		}

		public isElement(prop: '$touched'|'$error', element: FormElement): boolean|object {
			switch (FormElementType[element.type]) {
				case FormElementType.CheckList:
					let isMulti = element.columns.length > 1;

					switch (prop) {
						case '$touched':
							let touched = isMulti;

							for (let i = element.options.length - 1; i >= 0; --i) {
								let item = this.htmlForm[element.id + '_' + i];
								if (item) {
									if (isMulti) {
										touched = touched && item.$touched;
									} else {
										if (item.$touched) {
											return true;
										}
									}
								}
							}

							return touched;
						case '$error':
							let error = {};

							for (let i = element.options.length - 1; i >= 0; --i) {
								let item = this.htmlForm[element.id + '_' + i];
								if (item) {
									if (Object.keys(item.$error).length) {
										return item.$error;
									}
								}
							}

							return error;
					}
				default:
					return this.htmlForm[element.id] && this.htmlForm[element.id][prop];
			}
		}

		public showErrors(element: FormElement): object {
			if (this.attemptedSubmit || this.htmlForm.$submitted || this.isElement('$touched', element)) {
				return <object>this.isElement('$error', element);
			}

			return {};
		}

		public isHelpAbove(element: FormElement): boolean {
			switch (FormElementType[element.type]) {
				case FormElementType.CheckList:
				case FormElementType.PassFail:
					return true;
				default:
					return false;
			}
		}

		public setChecklistAnswer(element: FormElement, idx: number, value: any): void {
			let answerIdx = this.getAnswerIndex(element.id);

			this.instance.answers[answerIdx].selected[idx] =
				this.instance.answers[answerIdx].selected[idx] == value
					? null
					: value;

			if (element.columns.length <= 1) {
				for (let i = element.options.length - 1; i >= 0; --i) {
					this.htmlForm[element.id + '_' + i].$setTouched();
				}
			} else {
				this.htmlForm[element.id + '_' + idx].$setTouched();
			}
		}

		public isRequired(element: FormElement): boolean {
			if ((this.instance.version.read_only || element.read_only) && element.required) {
				switch (FormElementType[element.type]) {
					case FormElementType.CheckList:
						if (!element.required) {
							return false;
						}

						if (element.columns.length <= 1) {
							let answers = this.instance.answers[this.getAnswerIndex(element.id)].selected || [];
							for (let i = element.options.length - 1; i >= 0; --i) {
								if (angular.isDefined(answers[i]) && answers[i] !== null) {
									return false;
								}
							}

							return true;
						} else {
							return element.required;
						}

					default:
						return element.required;
				}
			}

			return false;
		}

		public getTypeForInput(element: FormElement): string {
			switch (FormElementType[element.type]) {
				case FormElementType.Number:
					return 'number';
				case FormElementType.Text:
					return 'text';
				default:
					return '';
			}
		}

		public getShowLabel(element: FormElement): boolean {
			switch (FormElementType[element.type]) {
				case FormElementType.StaticText:
					return element.show_name;
				case FormElementType.Checkbox:
				case FormElementType.Dropdown:
				case FormElementType.MultiSelect:
				case FormElementType.Number:
				case FormElementType.Text:
				case FormElementType.TextArea:
				case FormElementType.CheckList:
				case FormElementType.PassFail:
					return true;
				default:
					return false;
			}
		}

		public getShowNote(element: FormElement): boolean {
			switch (FormElementType[element.type]) {
				case FormElementType.PassFail:
					return true;
				default:
					return false;
			}
		}

		public getShowImageUpload(element: FormElement): boolean {
			switch (FormElementType[element.type]) {
				case FormElementType.PassFail:
					return true;
				default:
					return false;
			}
		}

		public getClassesForType(element: FormElement): string {
			switch (FormElementType[element.type]) {
				case FormElementType.Number:
				case FormElementType.Text:
				case FormElementType.TextArea:
				case FormElementType.Checkbox:
				case FormElementType.Dropdown:
				case FormElementType.MultiSelect:
				case FormElementType.PassFail:
					return 'left-indent';
				case FormElementType.StaticText:
				case FormElementType.Header:
				default:
					return '';
			}
		}

		public getClassesForInput(element: FormElement): string {
			switch (FormElementType[element.type]) {
				case FormElementType.StaticText:
					return 'mdl-typography--title';
				case FormElementType.Header:
					return 'mdl-typography--headline';
				case FormElementType.Number:
				case FormElementType.Text:
				case FormElementType.TextArea:
					return 'mdl-textfield__input';
				case FormElementType.Checkbox:
				case FormElementType.Dropdown:
				case FormElementType.MultiSelect:
				case FormElementType.PassFail:
				default:
					return '';
			}
		}

		public submitImage(elementId: number, file: File) {
			if (file !== null) {
				if (angular.isUndefined(this.files[elementId])) {
					this.files[elementId] = [];
				}

				this.files[elementId].push(file);
			}
		};

		public uploadImages(iId: number) {
			Object.keys(this.files).forEach((eId) => {
				Object.keys(this.files[eId]).forEach((index: any) => {
					this.UploadService.upload('forminstance/' + iId + '/element/' + eId + '/image', this.files[eId][index]);
				});
			});
		}

		public deleteUnsavedImage($index: number, eId: number) {
			this.files[eId].splice($index, 1);
		};

		public setImg(elementId: number) {
			angular.element(document.querySelector('#image-upload' + elementId)).triggerHandler('click');
		};
	}

	export class FormCreateController extends FormDetailControllerBase {
		public isNew: boolean = true;
		public isLoadingType: number = 0;
		public types: FormList[] = [];

		protected loadedForms: {[id: number]: Form} = {};

		protected getInstance(): ng.IPromise<FormInstance> {
			let params: any = {};

			if (this.isItemForm) {
				params.item_form = 1;
				params.for_item = this.$state.params.item;
			} else {
				params.sr_form = 1;
			}

			return this.FormService.getForms(params)
				.then((response) => {
					this.types = response.data;
					return this.$q.when(<FormInstance>{});
				});
		}

		protected saveInstance(data: any, email?: string, phone?: string): ng.IPromise<FormInstance> {
			data.service_request = this.service_request_id;
			if (this.isItemForm) {
				data.serviceable_item = this.$state.params.item;
			}

			return this.FormService
				.createInstance(
					this.instance.version.id,
					this.isItemForm ? 'serviceableitem' : 'servicerequest',
					data, {
						email: email,
						phone: phone
					})
				.then((resp) => resp.data);
		}

		private setType(type: Form): void {
			this.instance.version = type;
			this.instance.answers = [];
		}

		public selectType(type: FormList): void {
			if (this.isLoadingType) {
				return;
			}

			if (this.loadedForms[type.id]) {
				this.setType(this.loadedForms[type.id]);
				return;
			}

			this.isLoadingType = type.id;
			this.FormService.getForm(type.id)
				.then((response) => this.setType(response.data))
				.catch((err) => this.ToastService.showError('Error selecting form: ', err))
				.finally(() => this.isLoadingType = 0);
		}
	}

	export class FormEditController extends FormDetailControllerBase {
		public isNew: boolean = false;

		protected getInstance(): ng.IPromise<FormInstance> {
			return this.FormService.getInstance(this.$state.params.id).then((resp) => resp.data);
		}

		protected saveInstance(data: any, email?: string, phone?: string): ng.IPromise<FormInstance> {
			return this.FormService
				.updateInstance(this.instance.id, data, { email: email, phone: phone })
				.then((resp) => resp.data);
		}

		public deleteImage($index: number, images: FormAnswerImage[], image: FormAnswerImage) {
			this.FormService.deleteFormAnswerImage(this.instance.id, image.id).then(() => {
				images.splice($index, 1);
			});
		};
	}
}
